/**
 * Help and support on Mojo Up AI Cloud (docs/plans/2026-10-cloud-licences-and-operations.md,
 * section 3). A team asks Mojo Up from the console; the customer portal holds the ticket in its
 * staff queue and answers; Cloud shows the thread. Cloud is the customer's surface, the portal is
 * the system of record: Cloud keeps no ticket rows, it reads them from the portal when asked.
 *
 * The portal half is `packages/api/src/routes/integrations/cloud.ts` (tickets) in
 * mju-mojoupcustomerportal. Change both sides together.
 */
import { z } from "zod";
export const SUPPORT_PATH_SUFFIX = "/support";
/** `POST /internal/v1/support/changed`, from the portal, with the entitlement push's HMAC headers. */
export const SUPPORT_CHANGED_PATH = "/internal/v1/support/changed";
/** What the request is about: Mojo Up routes each kind to the right people. */
export const supportKindSchema = z.enum(["support", "bug", "billing"]);
export const SUPPORT_KIND_WORDS = { support: "Help with something", bug: "Something is broken", billing: "A billing question" };
/** The ticket's standing, as Cloud shows it (the portal maps its own statuses onto these). */
export const supportStatusSchema = z.enum(["open", "in_progress", "waiting", "resolved", "closed"]);
export const SUPPORT_STATUS_WORDS = { open: "Sent", in_progress: "Mojo Up is on it", waiting: "Waiting for you", resolved: "Resolved", closed: "Closed" };
/** Who asked, as Cloud knows them: never a portal account. */
export const supportRequesterSchema = z.object({
    id: z.string().min(1).max(100),
    name: z.string().max(200).optional(),
    email: z.string().email(),
});
export const supportMessageSchema = z.object({
    id: z.string(),
    at: z.string(),
    /** The customer's side, or Mojo Up's. */
    from: z.enum(["customer", "mojoup"]),
    authorName: z.string().optional(),
    text: z.string(),
});
export const supportTicketSchema = z.object({
    id: z.string(),
    /**
     * The organisation the portal holds it under. Cloud checks it against the organisation that asked before showing
     * the ticket, so a portal that answered for the wrong one is never believed; Cloud does not pass it on.
     */
    tenantId: z.string().optional(),
    /** The portal's reference people quote ("MU-4411"). */
    reference: z.string().optional(),
    kind: supportKindSchema,
    subject: z.string(),
    status: supportStatusSchema,
    requester: supportRequesterSchema,
    createdAt: z.string(),
    updatedAt: z.string(),
    /** When Mojo Up last wrote; absent until it has. */
    answeredAt: z.string().optional(),
    messages: z.array(supportMessageSchema).optional(),
});
// ── Console -> Cloud ──────────────────────────────────────────────────────────
export const supportCreateSchema = z.object({
    kind: supportKindSchema,
    subject: z.string().trim().min(3).max(200),
    message: z.string().trim().min(10).max(10_000),
});
export const supportReplySchema = z.object({ text: z.string().trim().min(1).max(10_000) });
/** `GET /cloud/v1/organisations/:id/support`. */
export const supportListSchema = z.object({
    /** False when this deployment has no portal integration: the page explains and gives the email instead. */
    configured: z.boolean(),
    tickets: z.array(supportTicketSchema),
});
// ── Cloud -> portal (server to server) ────────────────────────────────────────
export const portalTicketCreateSchema = z.object({
    tenantId: z.string().min(1).max(100),
    tenantName: z.string().min(1).max(200),
    requester: supportRequesterSchema,
    kind: supportKindSchema,
    subject: z.string().min(3).max(200),
    message: z.string().min(10).max(10_000),
    /** The team's plan at the time, so the queue can prioritise. */
    plan: z.enum(["free", "teams"]),
});
export const portalTicketMessageSchema = z.object({
    tenantId: z.string().min(1).max(100),
    requester: supportRequesterSchema,
    text: z.string().min(1).max(10_000),
});
export const portalTicketCloseSchema = z.object({ tenantId: z.string().min(1).max(100), requester: supportRequesterSchema });
export const portalTicketListResponseSchema = z.object({ tickets: z.array(supportTicketSchema) });
export const portalTicketResponseSchema = z.object({ ticket: supportTicketSchema });
// ── Portal -> Cloud ───────────────────────────────────────────────────────────
/** Ids only, signed; Cloud then reads the ticket and tells the requester. */
export const supportChangedSchema = z.object({
    tenantId: z.string().min(1).max(100),
    ticketId: z.string().min(1).max(100),
    event: z.enum(["replied", "status"]),
});
//# sourceMappingURL=support.js.map